import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Package, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';
import { getStatusConfig, fmtPrice, fmtDate } from '../lib/orderStatus';

/**
 * OrdersContent — Contenu du récapitulatif des commandes (Retailer).
 * Réutilisable dans ProfilePage (onglet Mes Commandes).
 */
const OrdersContent = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/purchase-orders').then(r => setOrders(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-sm text-slate-400">
        <div className="animate-spin h-5 w-5 border-2 border-slate-400 border-t-transparent rounded-full" />
        Chargement...
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-16" data-testid="orders-empty">
        <Package className="h-12 w-12 text-slate-200 mx-auto mb-3" />
        <p className="text-sm text-slate-400">Aucune commande pour le moment</p>
        <Link to="/dashboard/simulation">
          <Button variant="outline" className="mt-4 gap-2">
            <FileText className="h-4 w-4" /> Aller à la simulation
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="orders-list">
      {orders.map(po => {
        const st = getStatusConfig(po.status);
        const Icon = st.icon;
        const totals = po.confirmed_totals || po.snapshot_totals || {};
        const partner = po.snapshot_partner || {};
        const itemCount = (po.snapshot_items || []).length;
        const isModified = po.status === 'confirmed_modified';

        return (
          <Link key={po.id} to={`/dashboard/commandes/${po.id}`}
            className="block rounded-xl border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md transition-all"
            data-testid={`po-card-${po.order_number}`}>
            <div className="p-4 flex items-center gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center">
                <FileText className="h-5 w-5 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-slate-900">{po.order_number}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${st.color}`}>
                    <Icon className="h-3 w-3 inline mr-0.5" />{st.label}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {partner.nom || 'Distributeur'} &middot; {itemCount} produit{itemCount > 1 ? 's' : ''} &middot; {fmtDate(po.submitted_at || po.created_at)}
                </p>
                {isModified && (
                  <p className="text-[10px] text-blue-600 mt-0.5 font-medium">Des modifications ont été apportées par le distributeur</p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-bold text-indigo-700 tabular-nums">{fmtPrice(totals.total)}</p>
                <p className="text-[10px] text-slate-400">HT</p>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default OrdersContent;
